import React, { useState, useEffect } from 'react';
import { 
  Briefcase, Monitor, HardHat, Users, Activity, 
  Loader2, Calendar, MessageSquare, ArrowUpRight 
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    jobsTotal: 0,
    itJobs: 0,
    nonItJobs: 0,
    categories: {},
    applicationsTotal: 0,
    demosTotal: 0,
    enquiriesTotal: 0,
  });
  const [recentDemos, setRecentDemos] = useState([]);
  const [recentApps, setRecentApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // 1. Fetch Jobs from Firebase
        const jobsSnap = await getDocs(collection(db, 'jobs'));
        const jobsList = [];
        jobsSnap.forEach(d => jobsList.push(d.data()));

        const itCount = jobsList.filter(j => j.type?.toString().toUpperCase() === 'IT').length;
        const nonItCount = jobsList.length - itCount;
        const catMap = jobsList.reduce((acc, job) => {
          if (job.category) {
            acc[job.category] = (acc[job.category] || 0) + 1;
          }
          return acc;
        }, {});

        // 2. Fetch Applications from Firebase
        const appsSnap = await getDocs(collection(db, 'applications'));
        const appsList = [];
        appsSnap.forEach(d => appsList.push({ id: d.id, ...d.data() }));
        appsList.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

        // 3. Fetch Demos from Firebase
        const demosSnap = await getDocs(collection(db, 'demos'));
        const demosList = [];
        demosSnap.forEach(d => demosList.push({ id: d.id, ...d.data() }));
        demosList.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

        // 4. Fetch Enquiries from Firebase
        const enquiriesSnap = await getDocs(collection(db, 'enquiries'));
        const enquiriesList = [];
        enquiriesSnap.forEach(d => enquiriesList.push({ id: d.id, ...d.data() }));

        setStats({
          jobsTotal: jobsList.length,
          itJobs: itCount,
          nonItJobs: nonItCount,
          categories: catMap,
          applicationsTotal: appsList.length,
          demosTotal: demosList.length,
          enquiriesTotal: enquiriesList.length,
        });

        setRecentDemos(demosList.slice(0, 4));
        setRecentApps(appsList.slice(0, 4));
      } catch (error) {
        console.error("Dashboard fetch error from Firebase:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) return (
    <div className="flex flex-col items-center justify-center p-20 gap-3">
      <Loader2 className="animate-spin text-blue-600 h-10 w-10" />
      <span className="text-slate-400 text-sm">Aggregating live data from Firebase...</span>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-black text-slate-900">System Overview</h2>
          <p className="text-slate-500 text-sm">Live breakdown of GlobalEPRC platform metrics powered by Firebase.</p>
        </div>
        <div className="flex items-center gap-2 text-xs font-bold text-emerald-600 bg-emerald-50 px-3.5 py-1.5 rounded-full border border-emerald-200">
          <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></span>
          Firebase DB Connected
        </div>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <Link to="/admin/demos">
          <StatCard 
            icon={<Calendar className="text-amber-500" />} 
            label="Demo Bookings" 
            value={stats.demosTotal} 
            color="bg-amber-50 border-amber-200/80" 
            actionText="View Leads →"
          />
        </Link>

        <Link to="/admin/applications">
          <StatCard 
            icon={<Users className="text-blue-600" />} 
            label="Job Applications" 
            value={stats.applicationsTotal} 
            color="bg-blue-50 border-blue-200/80" 
            actionText="Review Resumes →"
          />
        </Link>

        <Link to="/admin/enquiries">
          <StatCard 
            icon={<MessageSquare className="text-emerald-600" />} 
            label="Contact Enquiries" 
            value={stats.enquiriesTotal} 
            color="bg-emerald-50 border-emerald-200/80" 
            actionText="Open Messages →"
          />
        </Link>

        <Link to="/admin/jobs">
          <StatCard 
            icon={<Briefcase className="text-purple-600" />} 
            label="Active Jobs" 
            value={stats.jobsTotal} 
            color="bg-purple-50 border-purple-200/80" 
            actionText="Manage Jobs →"
          />
        </Link>
      </div>

      {/* Quick Roles Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="p-6 bg-slate-900 text-white rounded-3xl flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-purple-400 uppercase tracking-widest">IT Openings</p>
            <p className="text-3xl font-black mt-1">{stats.itJobs}</p>
            <p className="text-xs text-slate-400 mt-1">Software, AI, DevOps & Cloud positions</p>
          </div>
          <Monitor className="h-10 w-10 text-purple-400 opacity-80" />
        </div>

        <div className="p-6 bg-slate-900 text-white rounded-3xl flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-orange-400 uppercase tracking-widest">Non-IT Trades</p>
            <p className="text-3xl font-black mt-1">{stats.nonItJobs}</p>
            <p className="text-xs text-slate-400 mt-1">Industrial, Engineering & Workforce positions</p>
          </div>
          <HardHat className="h-10 w-10 text-orange-400 opacity-80" />
        </div>
      </div>

      {/* Recent Activity: Latest Demos & Latest Applications */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Demo Inquiries */}
        <div className="bg-white p-6 rounded-3xl border border-slate-200/80 shadow-sm">
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
            <h3 className="font-bold text-slate-900 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-amber-500" /> Recent Demo Requests
            </h3>
            <Link to="/admin/demos" className="text-xs font-bold text-blue-600 hover:underline flex items-center gap-1">
              View All ({stats.demosTotal}) <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          {recentDemos.length === 0 ? (
            <p className="text-xs text-slate-400 py-6 text-center">No demo requests submitted yet.</p>
          ) : (
            <div className="space-y-3">
              {recentDemos.map(d => (
                <div key={d.id} className="p-3.5 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-between">
                  <div>
                    <p className="font-bold text-slate-900 text-sm">{d.name}</p>
                    <p className="text-xs text-slate-500">{d.email} • {d.phone || 'No phone'}</p>
                    <span className="inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                      {d.service}
                    </span>
                  </div>
                  <span className={`text-[10px] font-black uppercase px-2 py-1 rounded-lg ${
                    d.status === 'Completed' ? 'bg-emerald-100 text-emerald-700' :
                    d.status === 'Scheduled' ? 'bg-purple-100 text-purple-700' :
                    'bg-amber-100 text-amber-800'
                  }`}>
                    {d.status || 'New'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Job Applications */}
        <div className="bg-white p-6 rounded-3xl border border-slate-200/80 shadow-sm">
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
            <h3 className="font-bold text-slate-900 flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" /> Recent Job Applications
            </h3>
            <Link to="/admin/applications" className="text-xs font-bold text-blue-600 hover:underline flex items-center gap-1">
              View All ({stats.applicationsTotal}) <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          {recentApps.length === 0 ? (
            <p className="text-xs text-slate-400 py-6 text-center">No job applications submitted yet.</p>
          ) : (
            <div className="space-y-3">
              {recentApps.map(a => (
                <div key={a.id} className="p-3.5 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-between">
                  <div>
                    <p className="font-bold text-slate-900 text-sm">{a.name}</p>
                    <p className="text-xs text-slate-500">{a.email} • {a.number || 'No phone'}</p>
                    <span className={`inline-block mt-1 text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${
                      a.roleType === 'IT' ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'
                    }`}>
                      {a.jobTitle}
                    </span>
                  </div>
                  <span className="text-[10px] font-bold text-slate-500">
                    {a.createdAtString || 'Recent'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Category Breakdown Section */}
      {Object.keys(stats.categories).length > 0 && (
        <div className="bg-white p-8 rounded-3xl border border-slate-200/80 shadow-sm">
          <h3 className="text-lg font-bold mb-5 flex items-center gap-2 text-slate-800">
            <Activity className="h-5 w-5 text-amber-500" /> Jobs Category Distribution
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(stats.categories).map(([name, count]) => (
              <div key={name} className="p-4 rounded-2xl bg-slate-50 border border-slate-100">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-tighter truncate">{name || 'General'}</p>
                <p className="text-2xl font-black text-slate-800 mt-0.5">{count}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper Component for the Stat Cards
const StatCard = ({ icon, label, value, color, actionText }) => (
  <div className={`p-6 rounded-3xl border ${color} flex flex-col justify-between shadow-sm hover:shadow-md transition cursor-pointer h-full`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-slate-500 font-bold text-xs uppercase tracking-wider mb-1">{label}</p>
        <p className="text-3xl font-black text-slate-900">{value}</p>
      </div>
      <div className="p-3 bg-white rounded-2xl shadow-sm">{icon}</div>
    </div>
    {actionText && (
      <div className="mt-4 pt-3 border-t border-slate-200/60 flex items-center text-xs font-bold text-slate-700">
        {actionText}
      </div>
    )}
  </div>
);

export default AdminDashboard;
